(() => {
  'use strict';

  const root = document.documentElement;
  root.classList.remove('no-js');
  root.classList.add('js');

  if (window.__ffgStorefrontReady) return;
  window.__ffgStorefrontReady = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canObserve = !reduceMotion && 'IntersectionObserver' in window;
  const initializedReveals = new WeakSet();
  const initializedCounts = new WeakSet();
  const initializedMarquees = new WeakSet();
  const railCleanups = new WeakMap();
  const pendingCounts = new Set();
  const observedItems = new Set();
  const countFrames = new WeakMap();

  const findInScope = (rootEl, selector) => {
    const items = [];
    if (rootEl instanceof Element && rootEl.matches(selector)) items.push(rootEl);
    if (rootEl.querySelectorAll) items.push(...rootEl.querySelectorAll(selector));
    return items;
  };

  const setTextIfChanged = (element, value) => {
    if (element.textContent !== value) element.textContent = value;
  };

  const formatCount = (value) => String(Math.round(value));

  const runCount = (element) => {
    if (!pendingCounts.has(element)) return;
    pendingCounts.delete(element);

    const target = Number(element.dataset.count);
    if (!Number.isFinite(target) || reduceMotion) {
      if (Number.isFinite(target)) setTextIfChanged(element, formatCount(target));
      return;
    }

    const duration = 1600;
    const startedAt = performance.now();

    const tick = (now) => {
      const elapsed = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - elapsed, 4);
      setTextIfChanged(element, formatCount(target * eased));
      if (elapsed < 1) {
        countFrames.set(element, requestAnimationFrame(tick));
      } else {
        countFrames.delete(element);
      }
    };

    setTextIfChanged(element, '0');
    countFrames.set(element, requestAnimationFrame(tick));
  };

  const observer = canObserve ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const target = entry.target;
      const isCount = target.matches('[data-count], .ffg-count');

      const tall = entry.boundingClientRect.height > window.innerHeight * 0.6;
      if (target.matches('.ffg-reveal') && (entry.intersectionRatio >= 0.15 || (tall && entry.isIntersecting))) {
        target.classList.add('is-visible');
        if (!isCount) {
          observer.unobserve(target);
          observedItems.delete(target);
        }
      }

      if (isCount && entry.intersectionRatio >= 0.6) {
        runCount(target);
        observer.unobserve(target);
        observedItems.delete(target);
      }
    });
  }, {
    threshold: [0, 0.15, 0.6],
    rootMargin: '0px 0px -10% 0px'
  }) : null;

  const initRevealAndCount = (rootEl) => {
    findInScope(rootEl, '.ffg-reveal').forEach((item) => {
      if (initializedReveals.has(item)) return;
      initializedReveals.add(item);

      if (!observer) {
        item.classList.add('is-visible');
        return;
      }

      observer.observe(item);
      observedItems.add(item);
    });

    findInScope(rootEl, '[data-count], .ffg-count').forEach((item) => {
      if (initializedCounts.has(item)) return;
      initializedCounts.add(item);
      pendingCounts.add(item);

      if (!observer) {
        runCount(item);
        return;
      }

      observer.observe(item);
      observedItems.add(item);
    });
  };

  const initMarquee = (marquee) => {
    if (initializedMarquees.has(marquee)) return;
    const track = marquee.querySelector('[data-marquee-track]');
    if (!track) return;

    initializedMarquees.add(marquee);
    const halfWidth = track.scrollWidth / 2;
    const duration = Math.min(40, Math.max(20, halfWidth / 38));
    track.style.setProperty('--marquee-duration', `${duration}s`);

    const toggle = () => {
      const paused = marquee.classList.toggle('is-paused');
      marquee.setAttribute('aria-pressed', paused ? 'true' : 'false');
    };
    marquee.setAttribute('aria-pressed', 'false');
    marquee.addEventListener('click', toggle);
    marquee.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggle();
      }
    });
  };

  const initRail = (rail) => {
    if (railCleanups.has(rail)) return;

    const viewport = rail.querySelector('[data-rail-viewport]');
    const cards = Array.from(rail.querySelectorAll('[data-rail-card]'));
    const current = rail.querySelector('[data-rail-current]');
    const total = rail.querySelector('[data-rail-total]');
    if (!viewport || !cards.length || !current || !total) return;

    const pad = (number) => String(number).padStart(2, '0');
    setTextIfChanged(total, pad(cards.length));
    let railFrame = 0;

    const updateRail = () => {
      railFrame = 0;
      const viewportRect = viewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + viewportRect.width / 2;
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      cards.forEach((card, index) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(cardCenter - viewportCenter);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      setTextIfChanged(current, pad(nearestIndex + 1));
    };

    const onScroll = () => {
      if (railFrame) return;
      railFrame = requestAnimationFrame(updateRail);
    };

    viewport.addEventListener('scroll', onScroll, { passive: true });
    railCleanups.set(rail, () => {
      viewport.removeEventListener('scroll', onScroll);
      if (railFrame) cancelAnimationFrame(railFrame);
      railCleanups.delete(rail);
    });
    updateRail();
  };

  const initScope = (rootEl) => {
    initRevealAndCount(rootEl);
    findInScope(rootEl, '[data-ffg-marquee]').forEach(initMarquee);
    findInScope(rootEl, '[data-ffg-rail]').forEach(initRail);
  };

  const cleanupScope = (rootEl) => {
    findInScope(rootEl, '.ffg-reveal').forEach((item) => {
      if (observer) observer.unobserve(item);
      observedItems.delete(item);
      initializedReveals.delete(item);
    });

    findInScope(rootEl, '[data-count], .ffg-count').forEach((item) => {
      if (observer) observer.unobserve(item);
      observedItems.delete(item);
      pendingCounts.delete(item);
      initializedCounts.delete(item);
      const frame = countFrames.get(item);
      if (frame) cancelAnimationFrame(frame);
      countFrames.delete(item);
    });

    findInScope(rootEl, '[data-ffg-marquee]').forEach((marquee) => {
      initializedMarquees.delete(marquee);
    });

    findInScope(rootEl, '[data-ffg-rail]').forEach((rail) => {
      const cleanup = railCleanups.get(rail);
      if (cleanup) cleanup();
    });
  };

  initScope(document);
  document.addEventListener('shopify:section:load', (event) => initScope(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanupScope(event.target));

  const header = document.querySelector(
    '.shopify-section-group-header-group, [id^="shopify-section-sections--"][id*="__header"]'
  );
  if (header) {
    header.classList.add('ffg-header-target');
    let headerFrame = 0;
    const updateHeader = () => {
      headerFrame = 0;
      header.classList.toggle('ffg-header--scrolled', window.scrollY > 40);
    };
    window.addEventListener('scroll', () => {
      if (headerFrame) return;
      headerFrame = requestAnimationFrame(updateHeader);
    }, { passive: true });
    updateHeader();
  }
})();

/* FFG v2 — hero film: run-once guard, reduced motion, offscreen pause, section lifecycle */
(() => {
  'use strict';
  if (window.__ffgHeroVideoReady) return;
  window.__ffgHeroVideoReady = true;

  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const handled = new WeakSet();

  const detach = (video) => {
    video.removeAttribute('autoplay');
    video.pause();
    const source = video.querySelector('source');
    if (source) source.remove();
    video.removeAttribute('src');
    video.load();
  };

  const initVideos = () => {
    document.querySelectorAll('[data-ffg-hero-video]').forEach((video) => {
      if (handled.has(video)) return;
      handled.add(video);

      if (media.matches) {
        detach(video);
        return;
      }

      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const p = video.play();
              if (p && p.catch) p.catch(() => {});
            } else {
              video.pause();
            }
          });
        }, { threshold: 0.05 }).observe(video);
      }
    });
  };

  const onMotionChange = () => {
    if (!media.matches) return;
    document.querySelectorAll('[data-ffg-hero-video]').forEach(detach);
  };
  if (media.addEventListener) media.addEventListener('change', onMotionChange);

  initVideos();
  document.addEventListener('shopify:section:load', initVideos);
})();
